import type { LicenceId } from '@ps/platform-message-framework/common/LicenceId';
import { Sites } from '@ps/platform-message-framework/common/Sites';
import type { DeBlacklistBanStatus } from '@ps/platform-message-framework/maindbm/DeBlacklistBanStatus';
import type { DeOasisBonusEligibilty } from '@ps/platform-message-framework/maindbm/DeOasisBonusEligibilty';
import type { RegulatorAccValEventType } from '@ps/platform-message-framework/regulator/RegulatorAccValEventType';
import type { RegulatorAmlValEventType } from '@ps/platform-message-framework/regulator/RegulatorAmlValEventType';
import type { RegulatorBanPopupType } from '@ps/platform-message-framework/regulator/RegulatorBanPopupType';
import type { RegulatorPlayerValPopupType } from '@ps/platform-message-framework/regulator/RegulatorPlayerValPopupType';
import type { StatusCode } from '@ps/web-core-api';
import type {
  Currency,
  DeAddressVerificationResult,
  LifetimeLimitAckType,
  OddsFormat,
  OperatorGuardPlayerStatus
} from '@ps/web-core-enums';

import type { ErrorCollection } from './Response';

export type AccountSummaryCount = Record<string, number>;

export interface AccountSummary {
  buyInCount?: AccountSummaryCount;
  winLossCount?: AccountSummaryCount;
  depositCount?: AccountSummaryCount;
  cashOutCount?: AccountSummaryCount;
  winningsCount?: AccountSummaryCount;
  lossesCount?: AccountSummaryCount;
  type?: RegulatorPlayerValPopupType;
  days?: number;
}

export interface AccountCurrency {
  currency: Currency;
  chips: number;
  tMoney: number;
}

export interface EmailValidationFlags {
  verified: boolean;
  bounced: boolean;
  notAuthorized: boolean;
  alwaysValidated: boolean;
}

export interface PendingLimits {
  approved: boolean;
  amountNew: number;
  amountOld: number;
  currency: Currency;
  limitId: number;
  limitType: number;
  isDeposit?: boolean;
}

export interface Flag {
  hi: number;
  lo: number;
}

export interface AccountInfoLastLoginDetails {
  lastSession: boolean;
  reasonOfLogout: string;
  lastLogoutIp: string;
  lastLoginTime: number;
  lastLogoutTime: number;
}

export interface AccountInfoWinLossData {
  winLoss: number;
  currency: Currency;
  prevLoginTime: number;
}

export interface LoginLimitInfo {
  loginTotal?: number;
  loginTotalLimit?: number;
  loginTimeTotalSec?: number;
  loginTimeLimitMin?: number;
  userSessionStartTime?: number;
}

export interface OccupationInfo {
  occupation: string;
  occupationId?: string;
  companyName?: string;
  companyPhone?: string;
  companyAddress?: string;
  sourceOfFunds?: string;
  sourceOfWealth?: string;
  pepHioSelection?: string;
  pepHioName?: string;
  relationshipToPepHio?: string;
  playerIsNot3Party?: boolean;
  playerIsNotPEP?: boolean;
}

export interface PlaceOfBirthInfo {
  countryOfBirth: string;
  stateOfBirth: string;
  cityOfBirth: string;
}

export interface LugasPlayerStatusState {
  status: OperatorGuardPlayerStatus;
  lastAcknowledgementDate?: number;
}

export interface KBAQuestionType {
  choices: string[];
  question: string;
  questionID: number;
}

export interface KBAAnswerType {
  questionID: number;
  answer: string;
}

export interface Account {
  accountSummary: AccountSummary | null;
  accValEventType?: RegulatorAccValEventType;
  addr1: string;
  addr2: string;
  amlDaysLeft: number;
  amlDepositThresholdReachedTimestamp: number;
  amlEventType: RegulatorAmlValEventType;
  authLoginCode: number;
  autoAcceptPriceChanges: boolean;
  balances: AccountCurrency[];
  banPopUp: RegulatorBanPopupType;
  birthName: string;
  buildingNumber: string;
  citizenship?: string;
  city: string;
  country: string;
  dataIntegrityCheckFailed: boolean;
  dateOfBirth: number;
  deBlacklistBanStatus: DeBlacklistBanStatus;
  defaultCurrencyForCountry: Currency;
  deOasisBonusEligibility: DeOasisBonusEligibilty;
  depositConfirmLimits: PendingLimits[] | null;
  displayValueProposition: boolean;
  district?: string;
  dormantSuspended: boolean;
  dob: string;
  docExpiryDate: string;
  docIssueDate: string;
  docIssuePlace: string;
  docIssuingAuthority: number;
  docNumber: string;
  docType: number;
  docsDaysLeft: number;
  avcDaysLeft: number;
  email: string;
  esCitizenOrResident: boolean;
  eulaRequired: boolean;
  eulaSigned?: number;
  euSilentMigration?: boolean;
  firstName: string;
  flags2: Flag;
  flags3: Flag;
  flags: number;
  forcedCashout: boolean;
  fppAmount: number;
  fullName: string;
  gameConfirmLimits: PendingLimits[] | null;
  gamingAccountNumber?: string;
  gender: string | null;
  imageId: number;
  info: {
    casinoRealityCheck?: number | null;
    lastLoginDetails?: AccountInfoLastLoginDetails;
    sessionWinLossData?: AccountInfoWinLossData;
    loginLimitInfo?: LoginLimitInfo;
    confirmAccountDetails?: boolean;
  };
  infoSelfExclMsg: string | null;
  kbaQuestions: KBAQuestionType[];
  kbaAnswers: KBAAnswerType[];
  lastName: string;
  licenseId: LicenceId;
  lifetimeLimitAckAction: boolean;
  lifetimeLimitAckLifetimeLimit: number;
  lifetimeLimitAckTypeOfPopUp: LifetimeLimitAckType;
  middleName: string;
  migrationFlag: number;
  migrationMsg: null;
  migrationTarget: number;
  mobilePhone: string;
  monthlyNetDepositLimit?: number;
  netDepositLimitCurrency?: Currency;
  nationalDocumentNumber?: number | string;
  occupationInfo: OccupationInfo;
  oddsFormat: OddsFormat | null;
  orientationNumber?: string;
  partOfMunicipality?: string;
  passwordWeak: boolean;
  phone: string;
  placeOfBirth?: string;
  placeOfBirthInfo: PlaceOfBirthInfo | null;
  playChips: number;
  playerValPopupType?: RegulatorPlayerValPopupType;
  playerVerification: boolean;
  preventLogin: boolean;
  privileges2: Flag;
  privileges3: Flag;
  privileges: number;
  region?: string;
  registered?: number;
  reloader?: boolean;
  selfExcluded: boolean;
  selfExclusionExpirationDate: string | null;
  selfExclusionExpired: boolean;
  showBalance?: boolean;
  showCommunicationPreferencesPopup?: boolean;
  showUkaYoungUserDialog?: boolean;
  showReinstateDialog?: boolean;
  signEulaOptions: number;
  state: string;
  taxRegion?: string;
  streetName: string;
  suspendRMReasons: number;
  tempAccount: boolean;
  timestamp: number;
  timezone: number;
  totalBalance: number;
  totalBalanceInUsd: number;
  totalTMoney: number;
  totalTMoneyCurrency: Currency;
  userId: string;
  userIntId: number;
  verifyWithdrawalMethod: boolean;
  webClientDialog: string;
  webClientDialogFlag: number;
  zipCode: string;
  netDepositLimitGroup?: number;
  lugasPlayerStatus?: LugasPlayerStatusState;
  isRetryScreenPassed: boolean;
  communicationChannelsDisabled: number | null;
}

export const depositLimitExtendedSites = [
  Sites.COM,
  Sites.DE,
  Sites.DE_SH,
  Sites.DEN,
  Sites.SPA,
  Sites.EU,
  Sites.GR,
  Sites.NL,
  Sites.POR,
  Sites.ROM,
  Sites.SWE,
  Sites.UK,
  Sites.US_CO,
  Sites.USMI,
  Sites.US_NJ,
  Sites.US_PA,
  Sites.CA_ON
];
export interface Response {
  success: boolean;
  message?: string;
  errors?: ErrorCollection;
  statusCode?: StatusCode;
  messages?: { msg: { error: string } };
  deAddressVerificationResult?: DeAddressVerificationResult;
  kbaQuestions?: KBAQuestionType[];
  regAction?: number;
  ltVppInCents?: number;
}

export interface AccountInfo {
  name?: string;
  addr1?: string;
  city?: string;
  zipCode?: string;
  state?: string;
  country?: string;
  pfpChecked?: boolean;
  countryCode?: string;
  phone?: string;
}

export type AccountPayload =
  | (
      | { occupationInfo: OccupationInfo }
      | { accountInfo: AccountInfo }
      | { placeOfBirthInfo: PlaceOfBirthInfo }
      | { playerIsNotPEP: boolean; playerIsNot3Party: boolean; realMoneyFlag: boolean }
      | { realMoneyFlag: boolean }
      | { gender: string }
      | {
          mailListPoker: boolean;
          mailListCasino: boolean;
          mailListSports: boolean;
          liveAlertSport: boolean;
          communicationPreferencesPopupAlreadyShown?: boolean;
        }
      | {
          communicationChannelEmail: boolean;
          communicationChannelPushNotification: boolean;
        }
      | { kbaQuestions?: KBAQuestionType[] }
      | { kbaAnswers?: KBAAnswerType[] }
      | { email: Account['email'] }
      | { timezone?: string; timezoneName?: string }
      | { convertBuyIns: boolean; convertWinnings: boolean }
      | { mobilePhone?: Account['mobilePhone']; phone?: Account['phone'] }
      | { dob: string }
      | { autoAcceptPriceChanges: boolean }
      | { firstName: string; lastName: string; secondLastName?: string }
      | { oddsFormat: OddsFormat }
      | { middleName: string }
      | { birthName: string }
      | { citizenship: string; nationalDocumentNumber?: string }
      | {
          addr1?: string;
          streetAddress?: string;
          city?: string;
          state?: string;
          taxRegion?: string;
          zip: string;
          zipCode?: string;
          country: string;
          pfpChecked?: boolean;
          buildingNumber?: string;
          streetName?: string;
        }
      | {
          docExpiryDate: string;
          docIssueDate: string;
          docIssuePlace: string;
          docIssuingAuthority: number;
          docNumber: string;
          docType: number;
          docOtherTypeName?: string;
          personalIdType?: number;
        }
      | { nationalDocumentNumber: string }
      | { profession: string }
      | {
          question1: number;
          question2: number;
          answer1: string;
          answer2: string;
        }
    ) & { timeout?: number; fetchAccount?: boolean };

export interface AccountLocales {
  emLocale: number;
  emLocale2: number;
  emLocale3: number;
}

export enum DisplayTheme {
  None = 0,
  Light = 1,
  Dark = 2,
  Device = 3
}

export interface AccountDisplayTheme {
  theme: DisplayTheme;
}

export interface AccountHistoryStatementRequest {
  from: string;
  to: string;
  zipPassword: string;
  reportFormat: string;
  password: string;
}
